/**
 * Single collapsed pill that summarises all tool calls for the current
 * turn. Click to expand the per-call details.
 *
 * Replaces the earlier stack-of-rows UI: the agent often emits 6-12
 * tool calls per turn and stacking each as its own row pushed the
 * assistant's actual answer off-screen.
 */
import { useState } from 'react';
import type { ToolCallRecord } from './types';

export default function ToolCallsDisclosure({
  calls,
  inFlight,
}: {
  calls: ToolCallRecord[];
  inFlight: boolean;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!calls.length) return null;

  const running = calls.filter((c) => c.status === 'running').length;
  const done = calls.length - running;
  const label = inFlight && running > 0
    ? `Calling tools (${done}/${calls.length} done${running ? `, ${running} running` : ''})`
    : `${calls.length} tool call${calls.length === 1 ? '' : 's'}`;

  return (
    <div className="my-1.5 rounded border border-gray-200 bg-gray-50 text-[0.78rem]">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        className="flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-gray-700 hover:bg-gray-100"
      >
        <span className="flex items-center gap-1.5 truncate">
          <span className="font-mono text-[0.7rem] text-gray-500">
            {inFlight && running > 0 ? '⏳' : '✓'}
          </span>
          <span className="font-medium">{label}</span>
        </span>
        <span className="text-[0.65rem] text-gray-500">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="border-t border-gray-200 bg-white px-2 py-1.5">
          {calls.map((c) => (
            <div key={c.id} className="mb-1.5 last:mb-0">
              <div className="flex items-center gap-1.5 text-gray-700">
                <span className="font-mono text-[0.7rem] text-gray-500">
                  {c.status === 'running' ? '⏳' : '✓'}
                </span>
                <span className="font-medium">{c.name}</span>
                <span className="truncate text-gray-500">({summarise(c.input)})</span>
              </div>
              {c.output && (
                <pre className="ml-4 mt-0.5 max-h-32 overflow-auto rounded bg-gray-50 px-1.5 py-1 text-[0.68rem] leading-snug text-gray-700">
{JSON.stringify(c.output, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function summarise(input: Record<string, unknown>): string {
  return Object.entries(input)
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(', ');
}
